import { useState, useEffect } from "react";
import ShowcasePage from "@/pages/ShowcasePage";
import LandingPage from "@/pages/LandingPage";

const CASE_SLUG = "ins-mercato";

export default function GatedCasePage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if already unlocked via localStorage
    const unlocked = localStorage.getItem(`case_unlocked_${CASE_SLUG}`);
    setIsUnlocked(unlocked === "true");
    setIsChecking(false);
  }, []);

  // Listen for unlock event from ShowcasePage
  useEffect(() => {
    const handleUnlock = () => {
      setIsUnlocked(true);
    };

    window.addEventListener("case_unlocked", handleUnlock);
    return () => window.removeEventListener("case_unlocked", handleUnlock);
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4B4DF7]"></div>
      </div>
    );
  }

  // Show showcase/vetrina if not unlocked, otherwise show full landing
  return isUnlocked ? <LandingPage /> : <ShowcasePageWrapper onUnlock={() => { window.scrollTo(0, 0); setIsUnlocked(true); }} />;
}

// Wrapper to pass unlock callback to ShowcasePage
function ShowcasePageWrapper({ onUnlock }) {
  return <ShowcasePageInline onUnlock={onUnlock} />;
}

// Inline version of ShowcasePage that doesn't redirect but calls onUnlock
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

// Use relative URLs so API calls work on any domain (preview + production)
const API_URL = "";
const SKILLVUE_LOGO = "https://cdn.prod.website-files.com/63eb9c5c0665608db409b4df/66cdde23b308922774c4b3e0_logomark.svg";
const INS_LOGO = "https://customer-assets.emergentagent.com/job_2b924b31-3dec-4508-86fc-f552bf10515d/artifacts/o60ehn61_images%20%281%29.png";

const MAIN_STATS = [
  { value: "900+", label: "Assessment AI completati", color: "text-[#4B4DF7]" },
  { value: "95%", label: "Tasso di completamento", color: "text-[#4B4DF7]" },
  { value: "90", label: "Top Talent identificati", color: "text-[#4B4DF7]" },
];

const SECONDARY_STATS = [
  { value: "~1.000", label: "Collaboratori coinvolti" },
  { value: "47%", label: "Role-Ready immediato" },
  { value: "30min", label: "Durata assessment" },
  { value: "6", label: "Soft skill valutate" },
];

function ShowcasePageInline({ onUnlock }) {
  const [formData, setFormData] = useState({ nome: "", cognome: "", email: "" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.nome || !formData.cognome || !formData.email) {
      setError("Compila tutti i campi");
      return;
    }

    if (!privacyAccepted) {
      setError("Devi accettare l'informativa sulla privacy");
      return;
    }

    if (!formData.email.includes("@") || !formData.email.includes(".")) {
      setError("Inserisci un'email valida");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          case_study_slug: CASE_SLUG,
          privacy_accepted: privacyAccepted,
        }),
      });

      if (res.ok) {
        localStorage.setItem(`case_unlocked_${CASE_SLUG}`, "true");
        onUnlock();
      } else {
        setError("Errore nel salvataggio. Riprova.");
      }
    } catch (err) {
      setError("Errore di connessione. Riprova.");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white font-['Inter_Tight']">
      {/* Preload the full interview video while user is on showcase */}
      <link rel="preload" href="/full-interview.mp4" as="video" type="video/mp4" />
      {/* Hero Section */}
      <section className="pt-8 pb-4 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          {/* Logos */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <img src={SKILLVUE_LOGO} alt="Skillvue" className="h-9" />
            <span className="text-slate-300 text-xl font-light mx-1">×</span>
            <img src={INS_LOGO} alt="In's Mercato" className="h-11" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a1a2e] leading-[1.15] mb-2 text-center"
          >
            Come IN's Mercato ha costruito una
            <br />
            pipeline interna di{" "}
            <span className="bg-gradient-to-r from-[#4B4DF7] to-[#8B8DF9] bg-clip-text text-transparent">Store Manager</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm text-slate-400 max-w-lg mx-auto mb-5 text-center"
          >
            Un assessment AI su scala nazionale per trasformare la gestione del
            talento da reattiva a predittiva.
          </motion.p>

          {/* Video - Centered */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-3xl mx-auto mb-5"
          >
            <div className="bg-[#1a1a40] rounded-2xl overflow-hidden shadow-2xl shadow-[#1a1a40]/20">
              <video
                data-testid="hero-video"
                className="w-full aspect-video object-cover"
                controls
                autoPlay
                muted
                loop
                playsInline
                src="/teaser-video.mp4"
              />
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <button
              data-testid="cta-scroll-to-form"
              onClick={() => document.getElementById('form-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#4B4DF7] hover:bg-[#3a3cd6] text-white font-semibold text-base rounded-xl px-8 py-3.5 inline-flex items-center gap-2 transition-colors shadow-lg shadow-[#4B4DF7]/25"
            >
              Leggi il case study completo <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Stats */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-14 md:gap-20 mb-6"
          >
            {MAIN_STATS.map((stat, i) => (
              <div key={i} className="text-center" data-testid={`main-stat-${i}`}>
                <p className={`text-4xl md:text-5xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
                <p className="text-[11px] text-slate-400 mt-1 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </motion.div>

          {/* Secondary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {SECONDARY_STATS.map((stat, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-xl px-5 py-3"
                data-testid={`secondary-stat-${i}`}
              >
                <p className="text-lg font-bold text-[#1a1a2e]">{stat.value}</p>
                <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Challenge & Evolution Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* La Sfida */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-8 bg-[#FF6550] rounded-full" />
                <h3 className="text-sm font-semibold text-[#FF6550] uppercase tracking-wider">
                  La Sfida
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Con <span className="text-[#FF6550] font-semibold">570+ punti vendita</span>, 
                un fatturato di{" "}
                <span className="font-semibold text-[#1a1a2e]">1,5 miliardi di euro</span>{" "}
                e oltre{" "}
                <span className="font-semibold text-[#1a1a2e]">4.200 dipendenti</span>,
                servivano Store Manager qualificati e allineati alla cultura
                aziendale. Senza una pipeline strutturata, ogni nuova apertura
                rischiava di sottoperformare.
              </p>
            </motion.div>

            {/* Evoluzione 2026 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-8 bg-emerald-500 rounded-full" />
                <h3 className="text-sm font-semibold text-emerald-500 uppercase tracking-wider">
                  What's Next
                </h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Assessment nativi via{" "}
                <span className="text-emerald-600 font-semibold">WhatsApp</span>{" "}
                per abbattere ogni barriera d'accesso nella GDO. Estensione della
                valutazione agli Store Manager per costruire la pipeline dei
                futuri{" "}
                <span className="text-emerald-600 font-semibold">Area Manager</span>.
                Selezione e sviluppo integrati in un unico ciclo continuo.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-4xl text-slate-300 mb-6">"</p>
            <p className="text-xl md:text-2xl text-slate-700 italic leading-relaxed mb-8">
              Il progetto con Skillvue ci ha permesso di costruire per la prima
              volta una pipeline interna di Store Manager, collegando la gestione
              del talento direttamente al nostro piano di espansione.
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-semibold">
                MT
              </div>
              <div className="text-left">
                <p className="font-semibold text-[#1a1a2e]">Mirko Tortolano</p>
                <p className="text-sm text-slate-500">
                  Direttore Responsabile, IN's Mercato
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Form Section - at the bottom */}
      <section id="form-section" className="py-20 bg-[#1a1a2e]">
        <div className="max-w-xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Leggi il Case Study Completo
            </h2>
            <p className="text-slate-400 mb-8">
              Lascia i tuoi dati per accedere a tutti i risultati, la metodologia
              e i prossimi step.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-left">
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                    Nome
                  </label>
                  <Input
                    data-testid="input-nome"
                    type="text"
                    placeholder="Il tuo nome"
                    value={formData.nome}
                    onChange={(e) =>
                      setFormData({ ...formData, nome: e.target.value })
                    }
                    className="bg-[#252542] border-slate-700 text-white placeholder:text-slate-500 h-12"
                  />
                </div>
                <div className="text-left">
                  <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                    Cognome
                  </label>
                  <Input
                    data-testid="input-cognome"
                    type="text"
                    placeholder="Il tuo cognome"
                    value={formData.cognome}
                    onChange={(e) =>
                      setFormData({ ...formData, cognome: e.target.value })
                    }
                    className="bg-[#252542] border-slate-700 text-white placeholder:text-slate-500 h-12"
                  />
                </div>
              </div>

              <div className="text-left">
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                  Email Aziendale
                </label>
                <Input
                  data-testid="input-email"
                  type="email"
                  placeholder="nome@azienda.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="bg-[#252542] border-slate-700 text-white placeholder:text-slate-500 h-12"
                />
              </div>

              <div className="flex items-start gap-3 text-left pt-2">
                <Checkbox
                  id="privacy"
                  data-testid="privacy-checkbox"
                  checked={privacyAccepted}
                  onCheckedChange={(checked) => setPrivacyAccepted(checked)}
                  className="mt-1 border-slate-600 data-[state=checked]:bg-[#4B4DF7] data-[state=checked]:border-[#4B4DF7]"
                />
                <label htmlFor="privacy" className="text-xs text-slate-400 leading-relaxed">
                  Ho letto l'{" "}
                  <a
                    href="https://www.skillvue.ai/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#4B4DF7] underline"
                  >
                    informativa sulla privacy
                  </a>{" "}
                  di Skillvue; acconsento a ricevere comunicazioni da Skillvue e
                  all'archiviazione e all'utilizzo delle mie informazioni di
                  contatto.*
                </label>
              </div>

              <p className="text-xs text-slate-500 text-left">
                Puoi annullare l'iscrizione a queste comunicazioni in qualsiasi
                momento.
              </p>

              {error && (
                <p data-testid="form-error" className="text-red-400 text-sm text-left">{error}</p>
              )}

              <Button
                data-testid="submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#4B4DF7] hover:bg-[#3a3cd6] text-white font-semibold text-base rounded-xl mt-4"
              >
                {loading ? (
                  "Caricamento..."
                ) : (
                  <>
                    Accedi al Case Study <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-[#1a1a2e] border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={SKILLVUE_LOGO} alt="Skillvue" className="h-5" />
            <span className="text-sm text-slate-400">Skillvue</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a
              href="https://www.skillvue.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-300"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.skillvue.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-300"
            >
              skillvue.ai
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
